/**
 * Logger centralizado. En desarrollo escribe a consola; en producción
 * delega a Sentry (si está inicializado) y silencia `info`/`debug`.
 *
 * Uso:
 *   import { logger } from '@shared/utils/logger';
 *   logger.error('No se pudo cargar leads', err, { leadType });
 */

type LogContext = Record<string, unknown>;

const isDev = import.meta.env.DEV;

interface SentryScope {
  setLevel: (level: string) => void;
  setContext: (key: string, value: unknown) => void;
}

interface SentryClient {
  captureException: (error: Error) => void;
  captureMessage: (message: string) => void;
  withScope: (callback: (scope: SentryScope) => void) => void;
}

interface WindowWithSentry {
  Sentry?: SentryClient;
}

async function captureToSentry(
  level: 'error' | 'warning' | 'info',
  message: string,
  err?: unknown,
  ctx?: LogContext
) {
  try {
    const Sentry = (window as WindowWithSentry).Sentry;
    if (!Sentry) return;
    Sentry.withScope((scope) => {
      scope.setLevel(level);
      if (ctx) scope.setContext('extra', ctx);
      if (err instanceof Error) {
        Sentry.captureException(err);
      } else {
        Sentry.captureMessage(message);
      }
    });
  } catch {
    // Sentry no está cargado; ignoramos.
  }
}

export const logger = {
  debug: (...args: unknown[]) => {
    // eslint-disable-next-line no-console
    if (isDev) console.debug('[debug]', ...args);
  },
  info: (...args: unknown[]) => {
    // eslint-disable-next-line no-console
    if (isDev) console.info('[info]', ...args);
  },
  warn: (message: string, ctx?: LogContext) => {
    if (isDev) console.warn('[warn]', message, ctx ?? '');
    captureToSentry('warning', message, undefined, ctx);
  },
  error: (message: string, err?: unknown, ctx?: LogContext) => {
    if (isDev) console.error('[error]', message, err, ctx ?? '');
    captureToSentry('error', message, err, ctx);
  },
};
